import {
  CalendarCheck,
  CheckCircle2,
  Clock3,
  HeartPulse,
  Home,
  UserPlus,
  UserRound,
} from "lucide-react";
import { Link } from "react-router-dom";

import type { DashboardEvent } from "@/api/dashboard";
import { Card } from "@/components/ui/card";

type Props = {
  events: DashboardEvent[];
};

export function RecentActivityCard({
  events,
}: Props) {
  return (
    <Card className="flex h-full min-h-[520px] flex-col overflow-hidden">
      <div className="flex items-center justify-between border-b border-slate-800 px-6 py-5">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-violet-500/15 text-violet-400">
            <Clock3 size={20} />
          </div>

          <div>
            <h2 className="text-xl font-semibold text-white">
              Recent Activity
            </h2>

            <p className="mt-1 text-sm text-slate-500">
              What&apos;s happening in your church
            </p>
          </div>
        </div>

        <Link
          to="/activity"
          className="text-sm font-medium text-violet-400 transition hover:text-violet-300"
        >
          View all
        </Link>
      </div>

      {events.length === 0 ? (
        <div className="flex flex-1 items-center justify-center p-8 text-center">
          <div>
            <CheckCircle2
              className="mx-auto text-emerald-500"
              size={30}
            />

            <p className="mt-4 font-medium text-white">
              No recent activity
            </p>

            <p className="mt-2 text-sm text-slate-400">
              Church activity will appear here as it happens.
            </p>
          </div>
        </div>
      ) : (
        <div className="min-h-0 flex-1 divide-y divide-slate-800 overflow-y-auto">
          {events.map((event) => {
            const visual = getVisual(event);

            const to = event.household_id
              ? `/households/${event.household_id}`
              : event.member_id
                ? `/members/${event.member_id}`
                : "/";

            return (
              <Link
                key={event.id}
                to={to}
                className="group flex items-center gap-4 px-6 py-4 transition-all duration-200 hover:bg-slate-800/40"
              >
                <div
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition duration-200 group-hover:scale-105 ${visual.className}`}
                >
                  {visual.icon}
                </div>

                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-white">
                    {event.title}
                  </p>

                  <p className="mt-1 line-clamp-1 text-sm text-slate-400">
                    {event.description}
                  </p>
                </div>

                <p className="shrink-0 text-xs text-slate-500">
                  {formatRelativeTime(event.created_at)}
                </p>
              </Link>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function getVisual(event: DashboardEvent) {
  const type = event.event_type.toUpperCase();

  if (
    type.includes("ATTENDANCE") ||
    type.includes("CHECK")
  ) {
    return {
      icon: <CalendarCheck size={19} />,
      className: "bg-emerald-500/15 text-emerald-400",
    };
  }

  if (
    type.includes("NEW_MEMBER") ||
    type.includes("JOIN")
  ) {
    return {
      icon: <UserPlus size={19} />,
      className: "bg-emerald-500/15 text-emerald-400",
    };
  }

  if (type.includes("HOUSEHOLD")) {
    return {
      icon: <Home size={19} />,
      className: "bg-amber-500/15 text-amber-400",
    };
  }

  if (type.includes("CARE")) {
    return {
      icon: <HeartPulse size={19} />,
      className: "bg-rose-500/15 text-rose-400",
    };
  }

  return {
    icon: <UserRound size={19} />,
    className: "bg-indigo-500/15 text-indigo-400",
  };
}

function formatRelativeTime(value: string) {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  const differenceMilliseconds =
    Date.now() - date.getTime();

  const differenceMinutes = Math.max(
    0,
    Math.floor(
      differenceMilliseconds / 60_000
    )
  );

  if (differenceMinutes < 1) {
    return "Now";
  }

  if (differenceMinutes < 60) {
    return `${differenceMinutes}m ago`;
  }

  const differenceHours = Math.floor(
    differenceMinutes / 60
  );

  if (differenceHours < 24) {
    return `${differenceHours}h ago`;
  }

  const differenceDays = Math.floor(
    differenceHours / 24
  );

  return `${differenceDays}d ago`;
}

